<template>
  <div class="config-multi-list" :style="cssVars">
    <custom-multi-select
      :options="normalizedList"
      :selected="selectedIds"
      :enable-search="true"
      :use-external-trigger="true"
      :hide-select-box="true"
      :dropdown-min-width="0"
      placeholder="Select..."
      font-size="var(--text-xs2)"
      @input="selectItems"
    >
      <template #trigger>
        <div class="config-multi-list__trigger" role="button" tabindex="0">
          <span v-if="!selectedOptions.length" class="config-multi-list__placeholder">
            Select...
          </span>

          <span v-else class="config-multi-list__values">
            <span
              v-for="item in displayedOptions"
              :key="item.id"
              class="config-multi-list__tag"
            >
              <span class="config-multi-list__tag-text">{{ item.name }}</span>
            </span>
            <span v-if="hiddenOptionCount > 0" class="config-multi-list__tag config-multi-list__tag--overflow">
              +{{ hiddenOptionCount }}
            </span>
          </span>

          <span class="config-multi-list__caret">&#9662;</span>
        </div>
      </template>
    </custom-multi-select>
    <div ref="measure" class="config-multi-list__measure" aria-hidden="true">
      <span
        v-for="item in selectedOptions"
        :key="`measure-${item.id}`"
        class="config-multi-list__tag config-multi-list__tag--measure"
      >
        <span class="config-multi-list__tag-text">{{ item.name }}</span>
      </span>
      <span ref="overflowMeasure" class="config-multi-list__tag config-multi-list__tag--overflow config-multi-list__tag--measure">+99</span>
    </div>
  </div>
</template>

<script>
import CustomMultiSelect from '@/components/common/customMultiSelect.vue'
import { optionValue } from '../conditionHelper'

export default {
  name: 'ConfigMultiListElement',
  components: {
    CustomMultiSelect
  },
  data () {
    return {
      visibleOptionCount: 0,
      resizeObserver: null
    }
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    list: {
      type: Array,
      default: () => []
    },
    defaultStyle: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    normalizedList () {
      return this.list.map(item => {
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          return { id: item, name: String(item), value: item }
        }
        return {
          ...item,
          id: item.id ?? item.value ?? item.name,
          name: item.name ?? item.value ?? item.id
        }
      })
    },
    selectedIds () {
      return Array.isArray(this.value)
        ? this.value.map(item => optionValue(item)).filter(item => item !== undefined && item !== null && item !== '')
        : []
    },
    selectedOptions () {
      return this.selectedIds
        .map(id => this.normalizedList.find(item => optionValue(item) === id))
        .filter(Boolean)
    },
    displayedOptions () {
      return this.selectedOptions.slice(0, this.visibleOptionCount)
    },
    hiddenOptionCount () {
      return Math.max(this.selectedOptions.length - this.visibleOptionCount, 0)
    },
    cssVars () {
      return {
        '--config-list-border-color': this.defaultStyle.borderColor || '#dfe5ed',
        '--config-list-background': this.defaultStyle.backGroundColor || '#ffffff',
        '--config-list-min-height': this.defaultStyle.labelHeight || '30px',
        '--config-list-tag-bg': '#fff3bf',
        '--config-list-tag-color': '#8a5a00',
        '--config-list-tag-remove': '#b36b00',
        '--config-list-border-active': this.defaultStyle.borderColor || '#f59e0b',
        '--config-list-border-hover': '#f8b84e'
      }
    }
  },
  watch: {
    selectedOptions () {
      this.$nextTick(() => this.calculateVisibleOptions())
    }
  },
  methods: {
    selectItems (selectedIds) {
      const selectedOptions = selectedIds
        .map(id => this.normalizedList.find(item => optionValue(item) === id))
        .filter(Boolean)
      this.$emit('input', selectedOptions)
    },
    getMeasuredTagWidths () {
      const measureEl = this.$refs.measure
      if (!measureEl) {
        return []
      }
      return Array.from(measureEl.querySelectorAll('.config-multi-list__tag--measure'))
        .slice(0, this.selectedOptions.length)
        .map(element => Math.ceil(element.offsetWidth))
    },
    getOverflowBadgeWidth (hiddenCount) {
      const overflowMeasureEl = this.$refs.overflowMeasure
      if (!overflowMeasureEl) {
        return 36
      }
      overflowMeasureEl.textContent = `+${hiddenCount}`
      return Math.ceil(overflowMeasureEl.offsetWidth)
    },
    calculateVisibleOptions () {
      const triggerEl = this.$el?.querySelector('.config-multi-list__trigger')
      if (!triggerEl) {
        this.visibleOptionCount = this.selectedOptions.length
        return
      }

      if (!this.selectedOptions.length) {
        this.visibleOptionCount = 0
        return
      }

      const triggerStyle = window.getComputedStyle(triggerEl)
      const horizontalPadding = parseFloat(triggerStyle.paddingLeft || 0) + parseFloat(triggerStyle.paddingRight || 0)
      const gap = 6
      const caretWidth = 20
      const availableWidth = triggerEl.clientWidth - horizontalPadding - caretWidth - gap
      const tagWidths = this.getMeasuredTagWidths()

      let usedWidth = 0
      let visibleCount = 0

      for (let index = 0; index < tagWidths.length; index++) {
        const remainingCount = this.selectedOptions.length - (index + 1)
        const overflowWidth = remainingCount > 0 ? this.getOverflowBadgeWidth(remainingCount) + gap : 0
        const nextWidth = usedWidth + (visibleCount > 0 ? gap : 0) + tagWidths[index]

        if (nextWidth + overflowWidth > availableWidth) {
          break
        }

        usedWidth = nextWidth
        visibleCount += 1
      }

      this.visibleOptionCount = visibleCount > 0 ? visibleCount : 1
    },
    setupResizeObserver () {
      if (typeof ResizeObserver === 'undefined' || !this.$el) {
        return
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.calculateVisibleOptions()
      })
      this.resizeObserver.observe(this.$el)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.calculateVisibleOptions()
      this.setupResizeObserver()
      window.addEventListener('resize', this.calculateVisibleOptions)
    })
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    window.removeEventListener('resize', this.calculateVisibleOptions)
  }
}
</script>

<style lang="scss" scoped>
.config-multi-list {
  width: 100%;
}

.config-multi-list__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: var(--config-list-min-height);
  border: 1px solid var(--config-list-border-color);
  border-radius: 4px;
  background: var(--config-list-background);
  padding: 3px 8px;
  color: #101828;
  cursor: pointer;
  font-size: var(--text-xs2);
  text-align: left;

  &:hover {
    border-color: var(--config-list-border-hover);
    box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.18);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--config-list-border-active);
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
  }
}

.config-multi-list__values {
  display: flex;
  flex: 1;
  flex-wrap: nowrap;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.config-multi-list__placeholder {
  flex: 1;
  min-width: 0;
  color: #8a94a6;
}

.config-multi-list__tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 1px solid rgba(245, 158, 11, 0.28);
  border-radius: 6px;
  background: var(--config-list-tag-bg);
  color: var(--config-list-tag-color);
  padding: 4px 8px;
  line-height: 1;
  flex: 0 0 auto;
}

.config-multi-list__tag-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.config-multi-list__tag--overflow {
  font-weight: 500;
}

.config-multi-list__caret {
  flex: 0 0 auto;
  color: #8a94a6;
}

.config-multi-list__measure {
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  height: 0;
  overflow: hidden;
}

.config-multi-list__tag--measure {
  margin-right: 6px;
}

.config-multi-list ::v-deep .multiselect__trigger {
  width: 100%;
}

.config-multi-list ::v-deep .dropdown {
  z-index: 1100;
  margin-top: -5px;
}

.config-multi-list ::v-deep .dropdown.open-up {
  margin-top: 0;
  margin-bottom: -5px;
}

.config-multi-list ::v-deep .dropdown-search {
  padding: 6px 8px;
}

.config-multi-list ::v-deep .dropdown-search input {
  height: 30px;
  font-size: var(--text-xs2);
}
</style>
